import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { handleApiError } from '../../utils/errorHandler';
import { useNavigate } from 'react-router-dom';
import { Tag, Users, CalendarClock } from 'lucide-react';
import DataTable from '../DataTable';
import {
  discountsApi,
  DiscountDto,
  DiscountSummaryDto,
  formatCurrency,
  formatDate,
} from '../../services/discountsService';

const Container = styled.div`
  padding: 24px 0;
`;

const SummarySection = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const StatCard = styled.div<{ $color: string }>`
  flex: 1;
  min-width: 200px;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-right: 3px solid ${props => props.$color};
  border-radius: var(--border-radius-md);
  padding: 16px 20px;

  @media (max-width: 767px) {
    min-width: 100%;
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const StatTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const StatIcon = styled.div<{ $color: string }>`
  color: ${props => props.$color};
  opacity: 0.5;
`;

const StatValue = styled.div<{ $color: string }>`
  font-size: 26px;
  font-weight: 700;
  color: ${props => props.$color};
  direction: ltr;
  text-align: right;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const PAGE_SIZE = 20;

const DiscountsTab: React.FC = () => {
  const navigate = useNavigate();

  const [discounts, setDiscounts] = useState<DiscountDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState<DiscountSummaryDto | null>(null);

  // debounce לחיפוש (~300ms)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const loadDiscounts = useCallback(async () => {
    try {
      setLoading(true);
      const result = await discountsApi.listAll({
        page,
        pageSize: PAGE_SIZE,
        searchTerm: debouncedSearch || undefined,
      });
      setDiscounts(result.items);
      setTotalCount(result.totalCount);
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה בטעינת ההנחות'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await discountsApi.getSummary();
      setSummary(data);
    } catch {
      // הסיכום אינו חוסם את הטבלה
    }
  }, []);

  useEffect(() => {
    loadDiscounts();
  }, [loadDiscounts]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const columns = [
    { key: 'clientName', title: 'לקוח' },
    {
      key: 'amount',
      title: 'סכום',
      render: (value: number) => formatCurrency(value),
    },
    { key: 'typeName', title: 'סוג' },
    {
      key: 'discountDate',
      title: 'תאריך',
      render: (value: string) => formatDate(value),
    },
    {
      key: 'endDate',
      title: 'תאריך סיום',
      render: (value: string | null) => formatDate(value),
    },
    {
      key: 'effectiveAmount',
      title: 'הנחה אפקטיבית',
      render: (value: number) => formatCurrency(value),
    },
  ];

  return (
    <Container>
      <SummarySection>
        <StatCard $color="var(--primary-color)">
          <StatHeader>
            <StatTitle>סך ההנחות</StatTitle>
            <StatIcon $color="var(--primary-color)">
              <Tag size={20} strokeWidth={2} />
            </StatIcon>
          </StatHeader>
          <StatValue $color="var(--primary-color)">
            {formatCurrency(summary?.totalDiscounts ?? 0)}
          </StatValue>
        </StatCard>

        <StatCard $color="#16a34a">
          <StatHeader>
            <StatTitle>לקוחות שקיבלו הנחה</StatTitle>
            <StatIcon $color="#16a34a">
              <Users size={20} strokeWidth={2} />
            </StatIcon>
          </StatHeader>
          <StatValue $color="#16a34a">{summary?.clientsWithDiscountsCount ?? 0}</StatValue>
        </StatCard>

        <StatCard $color="#d97706">
          <StatHeader>
            <StatTitle>הנחות חודשיות</StatTitle>
            <StatIcon $color="#d97706">
              <CalendarClock size={20} strokeWidth={2} />
            </StatIcon>
          </StatHeader>
          <StatValue $color="#d97706">{summary?.monthlyDiscountsCount ?? 0}</StatValue>
        </StatCard>
      </SummarySection>

      <DataTable<DiscountDto>
        data={discounts}
        columns={columns}
        loading={loading}
        searchPlaceholder="חיפוש לפי שם לקוח..."
        onSearch={setSearchInput}
        onRowClick={(record) => navigate(`/clients/${record.clientId}/manage/billing`)}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: totalCount,
          onChange: (newPage) => setPage(newPage),
        }}
      />
    </Container>
  );
};

export default DiscountsTab;
